/** Shared helpers for Editor.js custom media blocks (vanilla DOM). */
import { API_URL } from '../../../config';

export function escapeAttr(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;');
}

export function formatFileSize(bytes) {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return String(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export async function uploadEditorMedia(spaceId, file, kind = 'auto') {
  if (!spaceId) throw new Error('Save the document in a space before uploading files.');
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/spaces/${encodeURIComponent(spaceId)}/editor-media`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      typeof data.detail === 'string'
        ? data.detail
        : Array.isArray(data.detail)
          ? data.detail.map((d) => d.msg || d.detail || '').filter(Boolean).join(', ')
          : data.message || res.statusText || 'Upload failed';
    throw new Error(msg || 'Upload failed');
  }
  return data;
}

export async function uploadSupportMedia(orgId, file, kind = 'auto') {
  if (!orgId) throw new Error('Organization is required for uploads.');
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/organizations/${encodeURIComponent(orgId)}/support/upload`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      typeof data.detail === 'string'
        ? data.detail
        : Array.isArray(data.detail)
          ? data.detail.map((d) => d.msg || d.detail || '').filter(Boolean).join(', ')
          : data.message || res.statusText || 'Upload failed';
    throw new Error(msg || 'Upload failed');
  }
  return data;
}

export function canUploadMedia(mediaUpload) {
  return !!(mediaUpload?.uploadFn || mediaUpload?.spaceId);
}

export async function performMediaUpload(mediaUpload, file, kind) {
  if (mediaUpload?.uploadFn) return mediaUpload.uploadFn(file, kind);
  return uploadEditorMedia(mediaUpload?.spaceId, file, kind);
}

/** Path extension from URL or filename (lowercase, no dot). */
export function fileExtensionFrom(nameOrUrl) {
  if (!nameOrUrl || typeof nameOrUrl !== 'string') return '';
  const noQuery = nameOrUrl.split('?')[0];
  const base = noQuery.split('/').pop() || '';
  const i = base.lastIndexOf('.');
  return i >= 0 ? base.slice(i + 1).toLowerCase() : '';
}

export function isDirectVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const ext = fileExtensionFrom(url);
  return ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'].includes(ext);
}

/** Tailwind classes for editor file-block icon tile (vanilla DOM). */
export function editorFileIconStyles(extRaw) {
  const ext = (extRaw || '').toLowerCase();
  const audio = new Set(['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac']);
  if (audio.has(ext)) {
    return { box: 'bg-violet-100 dark:bg-violet-900/30', stroke: 'text-violet-600 dark:text-violet-400' };
  }
  if (ext === 'pdf') return { box: 'bg-red-100 dark:bg-red-900/30', stroke: 'text-red-600 dark:text-red-400' };
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
    return { box: 'bg-blue-100 dark:bg-blue-900/30', stroke: 'text-blue-600 dark:text-blue-400' };
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return { box: 'bg-emerald-100 dark:bg-emerald-900/30', stroke: 'text-emerald-600 dark:text-emerald-400' };
  }
  if (['ppt', 'pptx', 'odp'].includes(ext)) {
    return { box: 'bg-orange-100 dark:bg-orange-900/30', stroke: 'text-orange-600 dark:text-orange-400' };
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return { box: 'bg-pink-100 dark:bg-pink-900/30', stroke: 'text-pink-600 dark:text-pink-400' };
  }
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz'].includes(ext)) {
    return { box: 'bg-amber-100 dark:bg-amber-900/30', stroke: 'text-amber-700 dark:text-amber-400' };
  }
  if (['json', 'xml', 'yaml', 'yml'].includes(ext)) {
    return { box: 'bg-slate-200 dark:bg-slate-700', stroke: 'text-slate-700 dark:text-slate-200' };
  }
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h', 'cs', 'php', 'sql', 'sh'].includes(ext)) {
    return { box: 'bg-indigo-100 dark:bg-indigo-900/30', stroke: 'text-indigo-600 dark:text-indigo-400' };
  }
  if (['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) {
    return { box: 'bg-fuchsia-100 dark:bg-fuchsia-900/30', stroke: 'text-fuchsia-600 dark:text-fuchsia-400' };
  }
  if (['md', 'txt', 'log'].includes(ext)) {
    return { box: 'bg-slate-100 dark:bg-slate-800', stroke: 'text-slate-600 dark:text-slate-300' };
  }
  return { box: 'bg-slate-100 dark:bg-slate-800', stroke: 'text-slate-500 dark:text-slate-400' };
}
